import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Container, Card } from 'react-bootstrap';
import { fetchEquipments, createEquipmentActivity } from './services/api';
import { Equipment, EquipmentActivity } from './types';
import { useNavigate } from 'react-router-dom';

const CreateEquipmentActivity: React.FC = () => {
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [equipmentId, setEquipmentId] = useState<number | ''>('');
  const [activityType, setActivityType] = useState('');
  const [control, setControl] = useState('');
  const [options, setOptions] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    fetchEquipments()
      .then(setEquipments)
      .catch(() => setError('Failed to fetch equipment'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      if (!equipmentId) throw new Error('Please select equipment');
      if (!control) throw new Error('Please select control type');
      const activity: EquipmentActivity = {
        equipment: Number(equipmentId),
        activity_name: activityType,
        control: control,
        options: options || undefined,
      };
      await createEquipmentActivity(activity);
      setSuccess(true);
      setTimeout(() => navigate('/equipments'), 1200);
    } catch (err: any) {
      setError(err.message || 'Failed to add activity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container className="p-4 p-md-5" style={{ maxWidth: 500 }}>
      <Card className="shadow-lg mt-4">
        <Card.Body>
          <h2 className="mb-4">Add Equipment Activity</h2>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="equipment">
              <Form.Label>Equipment</Form.Label>
              <Form.Select value={equipmentId} onChange={e => setEquipmentId(e.target.value === '' ? '' : Number(e.target.value))} required disabled={loading}>
                <option value="">Select Equipment</option>
                {equipments.map(eq => (
                  <option key={eq.id} value={eq.id}>{eq.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="activityType">
              <Form.Label>Activity Type</Form.Label>
              <Form.Control type="text" value={activityType} onChange={e => setActivityType(e.target.value)} required maxLength={100} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="control">
              <Form.Label>Control</Form.Label>
              <Form.Select value={control} onChange={e => setControl(e.target.value)} required>
                <option value="">Select Control Type</option>
                <option value="TEXT">TEXT</option>
                <option value="RADIO">RADIO</option>
                <option value="CHECK">CHECK</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="options">
              <Form.Label>Options</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3}
                value={options} 
                onChange={e => setOptions(e.target.value)} 
                placeholder="Enter options (e.g., for RADIO or CHECK controls, separate with commas)"
                maxLength={500}
              />
              <Form.Text className="text-muted">
                For RADIO/CHECK controls: separate options with commas (e.g., "Option 1, Option 2, Option 3")
              </Form.Text>
            </Form.Group>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">Activity added!</Alert>}
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => navigate('/equipments')} disabled={saving}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={saving}>{saving ? <Spinner animation="border" size="sm" /> : 'Save'}</Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CreateEquipmentActivity; 